import { Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import type { Unit } from "@/data/units";
import { formatBRL, previewPlans } from "@/data/plans";
import { track } from "@/lib/tracking";

export function UnitCard({
  unit,
  showAddress = true,
  showPlans = true,
  plansLabel = "Ver todos os planos",
}: {
  unit: Unit;
  showAddress?: boolean;
  showPlans?: boolean;
  plansLabel?: string;
}) {
  const plans = previewPlans(unit.slug);
  return (
    <article className="group overflow-hidden rounded-sm border border-border bg-card transition-colors hover:border-nook-green/50">
      <Link
        to="/unidades/$slug"
        params={{ slug: unit.slug }}
        onClick={() => track("unit_pricing_click", { unit: unit.slug, source_section: "unit_card" })}
        className="flex h-full flex-col"
      >
        <div className="aspect-[4/3] overflow-hidden">
          <img
            src={unit.images[0].src}
            alt={unit.images[0].alt}
            loading="lazy"
            decoding="async"
            className={`h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.04] ${
              unit.photosPending ? "scale-105 blur-md" : ""
            }`}
          />
        </div>
        <div className="flex flex-grow flex-col p-6">
          <div>
            <span className="eyebrow">{unit.neighborhood}</span>
            <h3 className="display mt-2 text-2xl">{unit.shortName}</h3>
            {showAddress && <p className="mt-3 text-xs text-muted-foreground">{unit.street}</p>}
          </div>

          {showPlans && (
            <div className="mt-6 border-t border-border pt-5">
              <p className="eyebrow">Planos</p>
              <dl className="mt-3 space-y-2.5">
                {plans.map((p) => (
                  <div key={p.id} className="flex items-baseline justify-between gap-4">
                    <dt className="text-[0.72rem] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                      {p.name}
                    </dt>
                    <dd className="whitespace-nowrap text-right text-sm text-foreground">
                      {formatBRL(p.monthly)}
                      <span className="text-xs text-muted-foreground">/mês</span>
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          )}

          <span className="mt-auto inline-flex min-h-11 items-center gap-2 pt-6 text-[0.72rem] font-semibold uppercase tracking-[0.16em] text-nook-green">
            {plansLabel} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </span>
        </div>
      </Link>
    </article>
  );
}
